/* CheckoutModal.jsx
 *
 * All customer-facing checkout UI extracted from NakshaStore.jsx (iter-5
 * partial modular split). Contains:
 *   • CheckoutModal — orchestrates the multi-step flow
 *   • DetailsStep / MethodStep / UPIStep / BankTransferStep /
 *     VerifyingStep / PausedStep / ReceiptPanel — step renderers
 *   • ComplianceDisclaimer + COA disclaimer text
 *   • PaymentProtectionBadge — reusable trust badge
 *   • loadRazorpayScript — Razorpay checkout.js loader
 *
 * Kept as named exports so call sites can grow without forcing additional
 * round-trip refactors. NakshaStore consumes only `CheckoutModal`.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { trackEvent, getStoredUtmParams } from "@/utils/analytics";
import {
  X,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Smartphone,
  Landmark,
  Clock,
  Download,
  ShieldCheck,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import { t } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_PHONE = "919717667115";
const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const applyDiscount = (price, settings) => {
  if (!settings) return Number(price || 0);
  const base = Number(settings.standard_price_override || 0) || Number(price || 0);
  if (!settings.discount_active || !settings.discount_percent) return base;
  const pct = Math.max(0, Math.min(100, Number(settings.discount_percent)));
  const out = Math.round((base * (100 - pct)) / 100);
  return out > 0 ? out : 0;
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// Reusable Payment Protection trust badge
const PaymentProtectionBadge = ({ lang }) => (
  <div
    className="rounded-2xl bg-gradient-to-br from-gold/10 via-white to-gold/5 border-2 border-gold/40 p-3 flex items-start gap-3"
    data-testid="payment-protection-badge"
  >
    <div className="h-10 w-10 rounded-xl bg-gold text-navy grid place-items-center shrink-0 shadow-[0_4px_10px_-2px_rgba(212,162,42,0.5)]">
      <ShieldCheck className="h-5 w-5" strokeWidth={2.4} />
    </div>
    <div className="min-w-0">
      <div className="text-[13px] font-extrabold text-navy leading-tight">
        100% Surakshit Payment
        <span className="text-gold-dark"> | Mera Naksha Bharosa</span>
      </div>
      <div className="mt-0.5 text-[11.5px] text-muted-foreground leading-snug">
        {lang === "hi"
          ? "आपका पैसा सुरक्षित है। स्क्रीनशॉट अपलोड करते ही हम तुरंत आपका नक्शा अनलॉक कर देंगे।"
          : "Aapka paisa surakshit hai. Screenshot upload karne ke baad hum turant aapka naksha unlock karenge."}
      </div>
    </div>
  </div>
);

const CheckoutModal = ({ design, lang, settings, onClose, onSuccess, receipt }) => {
  // Steps: "details" → "method" → "upi" → "uploading" → "verifying" → done (receipt set)
  const [step, setStep] = useState("details");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [intent, setIntent] = useState(null); // {receipt_no, vpa, upi_url, amount}
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotData, setScreenshotData] = useState(null); // base64 data URL
  const [upiRef, setUpiRef] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null); // {gateway, razorpay_key_id}
  // "upi" (default) or "bank_transfer". Determines which mode the
  // upload step renders + which `payment_method` we tag on the screenshot.
  const [chosenMethod, setChosenMethod] = useState("upi");
  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Fetch payment config once when modal opens
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/payment/config`)
      .then(({ data }) => {
        if (!cancel) setPaymentConfig(data);
      })
      .catch(() => {
        if (!cancel) setPaymentConfig({ gateway: "manual_upi", razorpay_key_id: "" });
      });
    return () => {
      cancel = true;
    };
  }, []);

  const validateDetails = () => {
    if (name.trim().length < 2) return setErr(t(lang, "store_err_name")) || false;
    if (!/^\+?\d{10,15}$/.test(phone.replace(/\s|-/g, "")))
      return setErr(t(lang, "store_err_phone")) || false;
    // Email is OPTIONAL (guest-flow pivot). Only validate format if filled.
    const trimmedEmail = email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return setErr(lang === "hi" ? "वैध email दर्ज करें या खाली छोड़ें" : "Enter a valid email or leave it blank") || false;
    }
    setErr("");
    return true;
  };

  const goToMethod = (e) => {
    e?.preventDefault();
    if (validateDetails()) setStep("method");
  };

  // Automated Razorpay flow — instant unlock on success.
  const startRazorpay = async () => {
    setPaying(true);
    setErr("");
    trackEvent("checkout_started", {
      design_id: design.id,
      design_name: design.name,
      price: design.price,
    });
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("razorpay_load_failed");
      const { data: order } = await axios.post(
        `${API}/library/${design.id}/razorpay-order`,
        {
          customer_name: name.trim(),
          customer_whatsapp: phone.trim(),
          customer_email: email.trim() || undefined,
        }
      );
      trackEvent("razorpay_payment_opened", {
        design_id: design.id,
        order_id: order.order_id,
        amount: order.amount,
      });
      const phoneDigits = phone.replace(/[^\d]/g, "").slice(-10);
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Mera Naksha",
        description: `${order.design_name} · ${design.size_label}`,
        order_id: order.order_id,
        prefill: {
          name: order.customer_name,
          contact: phoneDigits,
        },
        notes: { receipt_no: order.receipt_no },
        theme: { color: "#0B2545" },
        handler: async (response) => {
          // Razorpay returned success — verify on the backend, which unlocks
          // the plan files and returns the verified purchase.
          try {
            const { data: verified } = await axios.post(
              `${API}/library/razorpay-verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );
            onSuccess(verified);
            trackEvent("payment_successful", {
              design_id: design.id,
              order_id: order.order_id,
              amount: order.amount,
              payment_method: "razorpay",
            });
            trackEvent("purchase_completed", {
              design_id: design.id,
              design_name: order.design_name,
              amount: order.amount,
              transaction_id: verified?.receipt_no || order.order_id,
              ...getStoredUtmParams(),
            });
          } catch {
            setErr("Payment captured but verification failed. Contact support with your receipt no.");
            trackEvent("payment_failed", {
              design_id: design.id,
              order_id: order.order_id,
              error_reason: "verification_failed",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setErr("Payment cancelled. Try again or use UPI.");
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        setPaying(false);
        const e = resp?.error || {};
        // Surface Razorpay's exact code + description so the user (and us) know
        // why the modal rejected the payment. Test-mode rejections of real
        // instruments usually surface as code BAD_REQUEST_ERROR with reason
        // "payment_method_not_allowed" or similar — this makes that visible.
        const human = [e.description, e.reason, e.code].filter(Boolean).join(" · ");
        setErr(human || "Payment failed. Please try again.");
        trackEvent("payment_failed", {
          design_id: design.id,
          order_id: order.order_id,
          error_reason: e.code || e.reason || "unknown",
        });
        // Fire-and-forget telemetry so the admin can audit failure trends.
        axios
          .post(`${API}/library/payment-event`, {
            order_id: e.metadata?.order_id || order.order_id,
            payment_id: e.metadata?.payment_id || null,
            code: e.code || null,
            reason: e.reason || null,
            description: e.description || null,
            source: e.source || null,
            step: e.step || null,
          })
          .catch(() => {});
      });
      rzp.open();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        (e?.message === "razorpay_load_failed"
          ? "Could not load checkout. Please use UPI."
          : "Couldn't start checkout. Please use UPI.");
      setErr(msg);
      setPaying(false);
    }
  };

  // UPI flow — step 1: create intent
  const startUPI = async () => {
    setPaying(true);
    setErr("");
    setChosenMethod("upi");
    try {
      const { data } = await axios.post(`${API}/library/${design.id}/upi-intent`, {
        customer_name: name.trim(),
        customer_whatsapp: phone.trim(),
        customer_email: email.trim() || undefined,
      });
      setIntent(data);
      setStep("upi");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || t(lang, "store_err_generic"));
    } finally {
      setPaying(false);
    }
  };

  // Bank-transfer flow — reuses the upi-intent endpoint to mint the
  // receipt_no + purchase row, then jumps to a dedicated bank-info
  // upload screen. The screenshot submission gets tagged
  // `payment_method=bank_transfer` so admins see the right label.
  const startBank = async () => {
    setPaying(true);
    setErr("");
    setChosenMethod("bank_transfer");
    try {
      const { data } = await axios.post(`${API}/library/${design.id}/upi-intent`, {
        customer_name: name.trim(),
        customer_whatsapp: phone.trim(),
        customer_email: email.trim() || undefined,
      });
      setIntent(data);
      setStep("bank");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || t(lang, "store_err_generic"));
    } finally {
      setPaying(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please upload a screenshot image (PNG/JPG)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Screenshot must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshotData(reader.result);
      setScreenshotPreview(reader.result);
      setErr("");
    };
    reader.readAsDataURL(file);
  };

  const submitScreenshot = async () => {
    if (!screenshotData) {
      setErr("Please upload your payment screenshot first");
      return;
    }
    setUploading(true);
    setErr("");
    try {
      await axios.post(
        `${API}/library/purchases/${intent.receipt_no}/screenshot`,
        {
          screenshot_data_url: screenshotData,
          upi_reference: upiRef.trim(),
          payment_method: chosenMethod === "bank_transfer" ? "bank_transfer" : undefined,
        }
      );
      setStep("verifying");
    } catch {
      setErr("Upload failed — please try again");
    } finally {
      setUploading(false);
    }
  };

  // Poll for verification
  useEffect(() => {
    if (step !== "verifying" || !intent) return;
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/library/purchases/${intent.receipt_no}`
        );
        if (cancel) return;
        setPollStatus(data.payment_status);
        if (data.payment_status === "verified") {
          onSuccess(data);
          return;
        }
        if (data.payment_status === "rejected") {
          setErr(
            "Admin could not verify this payment. " +
              (data.admin_note ? `Reason: ${data.admin_note}` : "Please contact support.")
          );
          return;
        }
        pollTimerRef.current = setTimeout(poll, 5000);
      } catch {
        pollTimerRef.current = setTimeout(poll, 8000);
      }
    };
    poll();
    return () => {
      cancel = true;
      clearTimeout(pollTimerRef.current);
    };
  }, [step, intent, onSuccess]);

  // Auto-open the first plan when verified
  useEffect(() => {
    if (receipt?.plan_url) window.open(receipt.plan_url, "_blank", "noopener");
  }, [receipt]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="checkout-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl border-2 border-gold shadow-2xl overflow-hidden animate-fade-up max-h-[92vh] flex flex-col"
        data-testid="checkout-modal"
      >
        <div className="bg-navy text-white p-5 relative shrink-0">
          <button
            onClick={onClose}
            data-testid="checkout-close"
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="text-[11px] uppercase tracking-wider text-gold font-bold">
            {receipt ? t(lang, "store_receipt_kicker") : t(lang, "store_checkout_kicker")}
          </div>
          <h3 className="mt-1 text-xl font-extrabold">{design.name}</h3>
          <div className="mt-1 text-[12.5px] text-white/80">
            {design.size_label} • {design.area_sqft} {t(lang, "sqft")} • {design.bhk} BHK
          </div>
          <div className="mt-3 inline-flex items-center gap-2 flex-wrap">
            {(() => {
              const dPrice = applyDiscount(design.price, settings);
              const dHas = settings && settings.discount_active && dPrice < design.price && !(settings.standard_price_override > 0);
              return dHas ? (
                <>
                  <span className="px-3 py-1 rounded-full bg-gold text-navy font-extrabold text-sm tabular-nums">
                    {formatINR(dPrice)}
                  </span>
                  <span className="text-[12px] text-white/70 line-through tabular-nums">
                    {formatINR(design.price)}
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[11px] font-extrabold tracking-wider uppercase">
                    {settings.discount_percent}% OFF
                  </span>
                </>
              ) : (
                <span className="px-3 py-1 rounded-full bg-gold text-navy font-extrabold text-sm">
                  {formatINR(dPrice)}
                </span>
              );
            })()}
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
        {settings && settings.payments_enabled === false && !receipt ? (
          <PausedStep lang={lang} message={settings.payments_disabled_message} />
        ) : receipt ? (
          <ReceiptPanel receipt={receipt} design={design} lang={lang} />
        ) : step === "details" ? (
          <DetailsStep
            name={name} setName={setName}
            phone={phone} setPhone={setPhone}
            email={email} setEmail={setEmail}
            err={err}
            onSubmit={goToMethod}
            lang={lang}
          />
        ) : step === "method" ? (
          <MethodStep
            lang={lang}
            paying={paying}
            err={err}
            design={design}
            effectivePrice={applyDiscount(design.price, settings)}
            paymentConfig={paymentConfig}
            settings={settings}
            onRazorpay={startRazorpay}
            onUPI={startUPI}
            onBank={startBank}
            onBack={() => setStep("details")}
          />
        ) : step === "upi" ? (
          <UPIStep
            lang={lang}
            intent={intent}
            design={design}
            screenshotPreview={screenshotPreview}
            upiRef={upiRef} setUpiRef={setUpiRef}
            onPickFile={onPickFile}
            fileInputRef={fileInputRef}
            uploading={uploading}
            err={err}
            onSubmit={submitScreenshot}
            onBack={() => setStep("method")}
          />
        ) : step === "bank" ? (
          <BankTransferStep
            lang={lang}
            settings={settings}
            intent={intent}
            design={design}
            screenshotPreview={screenshotPreview}
            upiRef={upiRef} setUpiRef={setUpiRef}
            onPickFile={onPickFile}
            fileInputRef={fileInputRef}
            uploading={uploading}
            err={err}
            onSubmit={submitScreenshot}
            onBack={() => setStep("method")}
          />
        ) : step === "verifying" ? (
          <VerifyingStep
            intent={intent}
            pollStatus={pollStatus}
            err={err}
            lang={lang}
            customerName={name}
            design={design}
            paymentMethod={chosenMethod}
          />
        ) : null}
        </div>
      </div>
    </div>
  );
};

// Shown when admin has flipped the master payment toggle off.
const PausedStep = ({ lang, message }) => (
  <div className="p-6 space-y-4 text-center" data-testid="checkout-paused-step">
    <div className="h-16 w-16 mx-auto rounded-2xl bg-red-100 border-2 border-red-300 grid place-items-center">
      <Clock className="h-8 w-8 text-red-600" />
    </div>
    <h4 className="text-xl font-extrabold text-navy">
      {lang === "hi" ? "पेमेंट अस्थायी रूप से बंद" : "Payments temporarily paused"}
    </h4>
    <p className="text-[13.5px] text-muted-foreground max-w-prose mx-auto leading-relaxed">
      {message ||
        (lang === "hi"
          ? "हम जल्द ही पेमेंट फिर से शुरू करेंगे। कृपया कुछ देर बाद पुनः प्रयास करें।"
          : "We'll resume payments shortly. Please try again in a little while.")}
    </p>
    <a
      href={`https://wa.me/${WA_PHONE}?text=${encodeURIComponent(
        lang === "hi"
          ? "नमस्ते 🙏 मेरा नक्शा खरीदना है। पेमेंट कब शुरू होगा?"
          : "Hi 🙏 I'd like to buy a Mera Naksha plan. When will payments be live?"
      )}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="paused-wa-cta"
      className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] rounded-xl bg-whatsapp text-white text-[14px] font-extrabold hover:bg-whatsapp-hover"
    >
      <FaWhatsapp className="h-4 w-4" />
      {lang === "hi" ? "WhatsApp पर हमसे संपर्क करें" : "Contact us on WhatsApp"}
    </a>
  </div>
);

const DetailsStep = ({ name, setName, phone, setPhone, email, setEmail, err, onSubmit, lang }) => (
  <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="checkout-form">
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
        {t(lang, "store_field_name")}
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t(lang, "store_field_name_ph")}
        data-testid="checkout-name"
        className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-medium focus:border-navy focus:outline-none"
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
        {t(lang, "store_field_wa")}
      </label>
      <input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+91 98XXX XXXXX"
        data-testid="checkout-phone"
        className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-medium focus:border-navy focus:outline-none"
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold flex items-baseline justify-between">
        <span>{lang === "hi" ? "ईमेल (वैकल्पिक)" : "Email (optional)"}</span>
        <span className="text-[10px] normal-case tracking-normal text-muted-foreground/70 font-normal">
          {lang === "hi" ? "naksha PDF यहीं भेजा जाएगा" : "we'll email your plan here"}
        </span>
      </label>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        inputMode="email"
        data-testid="checkout-email"
        className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-medium focus:border-navy focus:outline-none"
      />
    </div>
    {err && (
      <div className="text-[12px] text-red-600 font-semibold" data-testid="checkout-error">
        {err}
      </div>
    )}
    <button
      type="submit"
      data-testid="checkout-continue-btn"
      className="w-full min-h-[56px] rounded-2xl font-extrabold text-[15px] flex items-center justify-center gap-2 bg-gold text-navy hover:bg-gold-dark hover:text-white shadow-[0_10px_20px_-10px_rgba(212,162,42,0.6)] active:scale-[0.99] transition-all"
    >
      Continue to Payment
      <ChevronRight className="h-5 w-5" />
    </button>
  </form>
);

const MethodStep = ({ lang, paying, err, design, effectivePrice, paymentConfig, settings, onRazorpay, onUPI, onBank, onBack }) => {
  const useRazorpay = paymentConfig && paymentConfig.gateway === "razorpay";
  const isTestMode = useRazorpay && paymentConfig.razorpay_mode === "test";
  const priceLabel = formatINR(effectivePrice ?? design.price);
  const bankEnabled = settings && settings.bank_transfer_enabled;
  return (
    <div className="p-5 space-y-4" data-testid="checkout-method-step">
      <div className="text-[12px] uppercase tracking-wider text-muted-foreground font-bold">
        Choose payment method
      </div>

      {isTestMode && (
        <div
          className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-[12px] leading-snug"
          data-testid="razorpay-test-mode-banner"
        >
          <div className="font-extrabold text-amber-900 uppercase tracking-wider text-[11px]">
            🧪 Razorpay Test Mode
          </div>
          <div className="text-amber-900/90 mt-1">
            Real cards / UPI IDs will be rejected. Use Razorpay test instruments:
          </div>
          <ul className="mt-1.5 list-disc pl-4 text-amber-900/90 space-y-0.5">
            <li>
              <span className="font-bold">UPI:</span>{" "}
              <code className="font-mono bg-amber-100 px-1 rounded">success@razorpay</code>
              {" "}(success) ·{" "}
              <code className="font-mono bg-amber-100 px-1 rounded">failure@razorpay</code>
              {" "}(failure)
            </li>
            <li>
              <span className="font-bold">Card:</span>{" "}
              <code className="font-mono bg-amber-100 px-1 rounded">4111 1111 1111 1111</code>
              {" "}· any future expiry · any CVV
            </li>
            <li>
              <span className="font-bold">Netbanking:</span> pick any bank, then click "Success".
            </li>
          </ul>
        </div>
      )}

      {useRazorpay ? (
        <button
          onClick={onRazorpay}
          disabled={paying}
          data-testid="pay-razorpay-btn"
          className="w-full rounded-2xl bg-gradient-to-br from-navy via-navy-deep to-navy text-gold p-4 text-left hover:shadow-[0_16px_32px_-12px_rgba(11,37,69,0.5)] disabled:opacity-50 transition-all border-2 border-gold relative overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gold/15 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-3 relative">
            <div className="h-12 w-12 rounded-xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
              <Smartphone className="h-6 w-6" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-[16px] tracking-tight text-white">
                Pay & Download Instantly
                <span className="ml-2 text-gold text-[10px] font-bold uppercase tracking-wider">Secure</span>
              </div>
              <div className="text-[12px] text-white/70 mt-0.5">
                UPI / Cards / Netbanking / Wallets · {priceLabel}
              </div>
            </div>
            {paying ? <Loader2 className="h-5 w-5 text-gold animate-spin" /> : <ChevronRight className="h-5 w-5 text-gold" />}
          </div>
        </button>
      ) : (
        <button
          onClick={onUPI}
          disabled={paying}
          data-testid="pay-via-upi-btn"
          className="w-full rounded-2xl bg-gradient-to-br from-navy via-navy-deep to-navy text-gold p-4 text-left hover:shadow-[0_16px_32px_-12px_rgba(11,37,69,0.5)] disabled:opacity-50 transition-all border-2 border-gold relative overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gold/15 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-3 relative">
            <div className="h-12 w-12 rounded-xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
              <Smartphone className="h-6 w-6" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-[16px] tracking-tight text-white">
                Pay via UPI
                <span className="ml-2 text-gold text-[11px] font-bold uppercase tracking-wider">Recommended</span>
              </div>
              <div className="text-[12px] text-white/70 mt-0.5">
                PhonePe / GPay / Paytm / BHIM • {priceLabel}
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gold" />
          </div>
        </button>
      )}

      {/* Trust badge directly under the primary CTA */}
      <PaymentProtectionBadge lang={lang} />

      {/* Optional Bank Transfer fallback — shown only when admin has
          activated it in Payment Settings. Mirrors the UPI flow but
          collects a bank-transfer screenshot instead of a QR payment. */}
      {bankEnabled && (
        <button
          onClick={onBank}
          disabled={paying}
          data-testid="pay-via-bank-btn"
          className="w-full rounded-2xl bg-white text-navy p-3.5 text-left border-2 border-navy/15 hover:border-navy disabled:opacity-50 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-navy text-gold grid place-items-center shrink-0">
              <Landmark className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-[14.5px] tracking-tight">
                Pay via Bank Transfer / NEFT / IMPS
              </div>
              <div className="text-[12px] text-muted-foreground mt-0.5">
                Transfer to our account → upload screenshot → admin verifies. Best for older devices.
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-navy" />
          </div>
        </button>
      )}

      {/* Legal-safe disclaimer near the Buy / Pay CTA */}
      <LegalDisclaimer lang={lang} variant="block" testId="method-step-disclaimer" />

      {err && (
        <div className="text-[12px] text-red-600 font-semibold text-center" data-testid="method-error">{err}</div>
      )}

      <button
        onClick={onBack}
        data-testid="back-to-details-btn"
        className="w-full text-[12.5px] text-muted-foreground hover:text-navy font-bold"
      >
        ← Edit name / phone
      </button>
    </div>
  );
};

/* BankTransferStep — alternative manual payment flow.
 * Shows the admin-configured bank account details, lets the customer
 * copy each field, then collects a screenshot just like UPIStep. The
 * receipt no comes from the same `upi-intent` endpoint so admins see
 * one unified inbox. The submitted screenshot is tagged
 * `payment_method=bank_transfer` so the admin can tell them apart.
 */
const BankTransferStep = ({
  lang,
  settings,
  intent,
  design,
  screenshotPreview,
  upiRef,
  setUpiRef,
  onPickFile,
  fileInputRef,
  uploading,
  err,
  onSubmit,
  onBack,
}) => {
  const [copied, setCopied] = useState(null);
  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("copy failed", e);
    }
  };
  const fields = [
    { key: "name", label: "Account name", value: settings?.bank_account_holder },
    { key: "acc", label: "Account number", value: settings?.bank_account_number },
    { key: "ifsc", label: "IFSC code", value: settings?.bank_ifsc },
    { key: "bank", label: "Bank name", value: settings?.bank_name },
  ].filter((f) => (f.value || "").trim());

  return (
    <div className="p-5 space-y-4" data-testid="checkout-bank-step">
      <div className="flex items-start gap-2 text-[12px] text-muted-foreground">
        <Landmark className="h-4 w-4 mt-0.5 text-navy" />
        <div>
          <div className="font-extrabold text-navy text-[14px]">Bank Transfer / NEFT / IMPS</div>
          <div>
            Transfer <strong className="text-navy">{formatINR(intent?.amount || design.price)}</strong> to the account below.
            Order ID: <span className="font-mono text-navy font-bold">{intent?.receipt_no}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-sand-alt border-2 border-border overflow-hidden">
        {fields.length === 0 ? (
          <div className="p-4 text-[13px] text-muted-foreground">
            Bank details are not configured. Please WhatsApp Team Mera Naksha for assistance.
          </div>
        ) : (
          fields.map((f) => (
            <div key={f.key} className="px-3.5 py-2.5 flex items-center justify-between gap-3 border-b border-border last:border-b-0">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">{f.label}</div>
                <div className="text-[14px] font-mono font-extrabold text-navy break-all">{f.value}</div>
              </div>
              <button
                type="button"
                onClick={() => copy(f.value, f.key)}
                data-testid={`bank-copy-${f.key}`}
                className="shrink-0 h-9 px-2.5 rounded-lg bg-white border-2 border-navy text-navy text-[12px] font-extrabold hover:bg-navy hover:text-gold transition-colors"
              >
                {copied === f.key ? "Copied!" : "Copy"}
              </button>
            </div>
          ))
        )}
      </div>

      {settings?.bank_notes && (
        <div className="rounded-xl bg-amber-50 border-2 border-amber-300 px-3 py-2 text-[12.5px] text-amber-900 leading-snug">
          <strong className="text-amber-900">Note:</strong> {settings.bank_notes}
        </div>
      )}

      <div className="rounded-xl bg-white border-2 border-border p-3 space-y-2">
        <div className="text-[10.5px] uppercase tracking-wider text-navy/70 font-extrabold">
          Step 2 — Upload payment screenshot or reference
        </div>
        <input
          type="text"
          placeholder="UTR / transaction reference (optional)"
          value={upiRef}
          onChange={(e) => setUpiRef(e.target.value)}
          data-testid="bank-ref-input"
          className="w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          data-testid="bank-screenshot-input"
          className="block w-full text-[12px] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-navy file:text-gold file:font-bold file:cursor-pointer cursor-pointer"
        />
        {screenshotPreview && (
          <img src={screenshotPreview} alt="screenshot preview" className="mt-2 max-h-44 rounded-lg border border-border" />
        )}
      </div>

      {err && (
        <div className="text-[12px] text-red-600 font-semibold" data-testid="bank-error">{err}</div>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="min-h-[44px] px-4 rounded-xl bg-white border-2 border-border text-navy font-bold text-[13px]">
          Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={uploading || !screenshotPreview}
          data-testid="bank-submit"
          className="flex-1 min-h-[44px] rounded-xl bg-gold text-navy font-extrabold text-[14px] inline-flex items-center justify-center gap-1.5 hover:bg-gold-dark hover:text-white disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {lang === "hi" ? "Screenshot Submit करें" : "Submit screenshot"}
        </button>
      </div>
    </div>
  );
};


const UPIStep = ({
  lang, intent, design, screenshotPreview, upiRef, setUpiRef,
  onPickFile, fileInputRef, uploading, err, onSubmit, onBack
}) => {
  const [copied, setCopied] = useState(false);
  const copyVPA = async () => {
    try {
      await navigator.clipboard.writeText(intent.vpa);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("clipboard copy failed", e);
    }
  };
  return (
    <div className="p-5 space-y-4" data-testid="checkout-upi-step">
      <button
        onClick={onBack}
        data-testid="upi-back-btn"
        className="text-[12.5px] text-muted-foreground hover:text-navy font-bold"
      >
        ← Change method
      </button>

      <div className="text-center">
        <div className="text-[11px] uppercase tracking-wider text-gold-dark font-bold">Step 1 of 2</div>
        <h4 className="mt-1 text-lg font-extrabold text-navy">Scan or open UPI app to pay</h4>
        <div className="text-[12.5px] text-muted-foreground mt-1">
          Receipt: <span className="font-mono font-bold text-navy">{intent.receipt_no}</span>
        </div>
      </div>

      {/* Bilingual How-to-Pay instructions — bold and easy to read for first-time users */}
      <div
        className="rounded-2xl bg-gold/10 border-2 border-gold/40 p-4"
        data-testid="upi-howto-instructions"
      >
        <div className="text-[14px] font-extrabold text-navy leading-tight">
          How to Pay &amp; Download
          <span className="text-gold-dark"> | पेमेंट कैसे करें</span>
        </div>
        <ol className="mt-3 space-y-2.5">
          <li className="flex items-start gap-2.5">
            <span className="h-5 w-5 rounded-full bg-navy text-gold text-[11px] font-extrabold grid place-items-center shrink-0 mt-0.5">
              1
            </span>
            <div className="text-[13px] leading-snug">
              <div className="font-bold text-navy">
                Scan the QR code or copy the UPI ID to pay via any app like GPay, PhonePe, or Paytm.
              </div>
              <div className="font-bold text-navy/85 mt-0.5">
                किसी भी UPI ऐप (GPay, PhonePe, Paytm) से पेमेंट करने के लिए QR कोड स्कैन करें या UPI ID कॉपी करें।
              </div>
            </div>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="h-5 w-5 rounded-full bg-navy text-gold text-[11px] font-extrabold grid place-items-center shrink-0 mt-0.5">
              2
            </span>
            <div className="text-[13px] leading-snug">
              <div className="font-bold text-navy">
                After payment, upload the screenshot of the successful transaction.
              </div>
              <div className="font-bold text-navy/85 mt-0.5">
                पेमेंट के बाद, सफल ट्रांज़ैक्शन का स्क्रीनशॉट यहाँ अपलोड करें।
              </div>
            </div>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="h-5 w-5 rounded-full bg-navy text-gold text-[11px] font-extrabold grid place-items-center shrink-0 mt-0.5">
              3
            </span>
            <div className="text-[13px] leading-snug">
              <div className="font-bold text-navy">
                We will verify and unlock your &lsquo;Design Mitra&rsquo; plan immediately.
              </div>
              <div className="font-bold text-navy/85 mt-0.5">
                हम वेरिफिकेशन के बाद आपका नक्शा तुरंत अनलॉक कर देंगे।
              </div>
            </div>
          </li>
        </ol>
      </div>

      {/* QR + VPA panel */}
      <div className="rounded-2xl bg-navy text-white p-4 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
        <div className="bg-white p-3 rounded-xl inline-block shadow-md" data-testid="upi-qr-wrapper">
          <QRCodeSVG
            value={intent.upi_url}
            size={192}
            level="M"
            includeMargin={false}
            bgColor="#FFFFFF"
            fgColor="#0B2545"
            data-testid="upi-qr-image"
          />
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-wider text-gold font-bold">UPI ID</div>
        <button
          onClick={copyVPA}
          data-testid="upi-vpa-copy"
          className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 transition-colors"
        >
          <span className="font-mono font-bold text-white">{intent.vpa}</span>
          <Copy className="h-3.5 w-3.5 text-gold" />
          {copied && <span className="text-[11px] text-emerald-300 font-bold">Copied!</span>}
        </button>
        <div className="mt-2 text-[12px] text-white/70">
          Amount: <span className="text-gold font-extrabold">{formatINR(intent.amount)}</span>
        </div>
      </div>

      {/* Mobile UPI app deep link (only useful on mobile, but works on all browsers) */}
      <a
        href={intent.upi_url}
        data-testid="upi-app-deeplink"
        className="w-full min-h-[48px] rounded-xl bg-gold text-navy font-extrabold text-[14px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white active:scale-[0.99] transition-all shadow-[0_8px_20px_-8px_rgba(212,162,42,0.6)]"
      >
        <Smartphone className="h-4 w-4" />
        Open in UPI App
      </a>

      {/* Trust badge — beside UPI button */}
      <PaymentProtectionBadge lang={lang} />

      {/* Step 2: screenshot upload */}
      <div className="pt-3 border-t border-border">
        <div className="text-[11px] uppercase tracking-wider text-gold-dark font-bold">Step 2 of 2</div>
        <h4 className="mt-1 text-base font-extrabold text-navy">Upload payment screenshot</h4>
        <div className="text-[11.5px] text-muted-foreground mt-0.5">
          Admin will verify within minutes and unlock your download.
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          data-testid="screenshot-file-input"
          className="hidden"
        />
        {!screenshotPreview ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="screenshot-pick-btn"
            className="mt-3 w-full min-h-[88px] rounded-2xl border-2 border-dashed border-navy/30 bg-sand-alt hover:bg-sand-alt/60 hover:border-navy/60 text-navy font-bold text-[13px] flex flex-col items-center justify-center gap-1.5 transition-all"
          >
            <Upload className="h-5 w-5 text-gold-dark" />
            Choose screenshot
            <span className="text-[10.5px] text-muted-foreground font-normal">PNG / JPG, up to 5MB</span>
          </button>
        ) : (
          <div className="mt-3 rounded-2xl overflow-hidden border-2 border-navy bg-navy/5 relative">
            <img
              src={screenshotPreview}
              alt="Screenshot preview"
              data-testid="screenshot-preview"
              className="w-full max-h-48 object-contain bg-white"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="screenshot-replace-btn"
              className="absolute top-2 right-2 px-2.5 py-1 rounded-full bg-navy text-gold text-[11px] font-bold hover:bg-navy-deep"
            >
              Change
            </button>
          </div>
        )}

        <input
          type="text"
          value={upiRef}
          onChange={(e) => setUpiRef(e.target.value)}
          placeholder="UPI Reference / Txn ID (optional)"
          data-testid="upi-reference-input"
          className="mt-3 w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-medium focus:border-navy focus:outline-none"
        />

        {err && (
          <div className="mt-2 text-[12px] text-red-600 font-semibold" data-testid="upi-error">{err}</div>
        )}

        <button
          onClick={onSubmit}
          disabled={uploading || !screenshotPreview}
          data-testid="screenshot-submit-btn"
          className={`mt-3 w-full min-h-[52px] rounded-2xl font-extrabold text-[14.5px] flex items-center justify-center gap-2 transition-all ${
            uploading || !screenshotPreview
              ? "bg-sand-alt text-muted-foreground cursor-not-allowed"
              : "bg-navy text-gold hover:bg-navy-deep shadow-[0_10px_20px_-10px_rgba(11,37,69,0.5)]"
          }`}
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ClipboardCheck className="h-5 w-5" />}
          {uploading ? "Uploading…" : "Submit for verification"}
        </button>
      </div>
    </div>
  );
};

const VerifyingStep = ({ intent, pollStatus, err, lang, customerName, design, paymentMethod }) => {
  // Build a richer customer-side support message that admins can act on
  // instantly: order #, name, design, amount, and the method (UPI vs Bank).
  const methodLabel =
    paymentMethod === "bank_transfer"
      ? (lang === "hi" ? "Bank Transfer / NEFT" : "Bank Transfer / NEFT")
      : "UPI";
  const waText =
    lang === "hi"
      ? `नमस्ते Team Mera Naksha 🙏\n` +
        `मैंने Order #${intent?.receipt_no || ''} का payment complete कर दिया है।\n` +
        `Name: ${customerName || '—'}\n` +
        `Design: ${design?.name || ''} (${design?.size_label || ''})\n` +
        `Amount: ₹${intent?.amount || design?.price || ''}\n` +
        `Payment Method: ${methodLabel}\n` +
        `कृपया screenshot verify करें और plan unlock करें।`
      : `Hello Team Mera Naksha 🙏\n` +
        `I have completed payment for Order #${intent?.receipt_no || ''}.\n` +
        `Name: ${customerName || '—'}\n` +
        `Design: ${design?.name || ''} (${design?.size_label || ''})\n` +
        `Amount: ₹${intent?.amount || design?.price || ''}\n` +
        `Payment Method: ${methodLabel}\n` +
        `Please verify my screenshot and unlock the plan.`;
  return (
  <div className="p-6 space-y-5 text-center" data-testid="checkout-verifying-step">
    <div className="h-16 w-16 mx-auto rounded-2xl bg-gold/15 border-2 border-gold/40 grid place-items-center">
      {err ? (
        <X className="h-8 w-8 text-red-600" />
      ) : (
        <Clock className="h-8 w-8 text-gold-dark animate-pulse" />
      )}
    </div>
    <div>
      <h4 className="text-xl font-extrabold text-navy">
        {err ? "Verification needed" : "Payment under review"}
      </h4>
      <p className="mt-1 text-[13px] text-muted-foreground max-w-prose mx-auto">
        {err
          ? err
          : "Hum aapka screenshot check kar rahe hain. Aapka naksha verify hote hi yahin unlock ho jayega — usually within minutes."}
      </p>
    </div>
    <div className="rounded-xl bg-sand-alt border border-border p-3 text-[12.5px] inline-block">
      <span className="text-muted-foreground">Receipt:</span>{" "}
      <span className="font-mono font-bold text-navy">{intent?.receipt_no}</span>
      {pollStatus && (
        <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-gold-dark font-bold">
          <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
          {pollStatus.replace(/_/g, " ")}
        </span>
      )}
    </div>
    <PaymentProtectionBadge lang={lang} />
    <a
      href={`https://wa.me/${WA_PHONE}?text=${encodeURIComponent(waText)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="verifying-wa-cta"
      className="inline-flex items-center justify-center gap-2 w-full min-h-[48px] rounded-xl bg-whatsapp text-white text-[14px] font-extrabold hover:bg-whatsapp-hover"
    >
      <FaWhatsapp className="h-5 w-5" />
      {lang === "hi"
        ? "WhatsApp पर Team को सूचित करें"
        : "Notify Team on WhatsApp"}
    </a>
    <div className="text-[11px] text-muted-foreground -mt-1">
      {lang === "hi"
        ? "तेज़ verification के लिए message भेजें — हमारी team तुरंत confirm करेगी।"
        : "Send the pre-filled message for fastest verification — our team will confirm shortly."}
    </div>
  </div>
  );
};

// Mandatory CoA-compliance disclaimer + acceptance shown above every plan
// download button. Customers must tick the checkbox before any plan PDF link
// becomes clickable. The disclaimer text is also burned into the downloaded
// PDF server-side (via /api/library/plan-download) so the legal footer
// follows the file even after offline sharing.
const COA_DISCLAIMER_EN =
  "This drawing is a standardized conceptual design intended for reference and preliminary planning only. " +
  "Site conditions, local bye-laws, structural safety, soil conditions, and municipal approval requirements " +
  "vary by location. Final execution drawings must be verified/adapted by licensed professionals as applicable.";
const COA_DISCLAIMER_HI =
  "यह drawing एक मानकीकृत conceptual design है जो केवल reference और preliminary planning के लिए है। " +
  "Site conditions, local bye-laws, structural safety, soil conditions और municipal approval requirements " +
  "हर location पर अलग होती हैं। Final execution drawings को licensed professionals द्वारा verify/adapt कराना अनिवार्य है।";

const ComplianceDisclaimer = ({ lang, accepted, onAccept }) => (
  <div
    className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3"
    data-testid="coa-disclaimer-block"
  >
    <div className="flex items-start gap-2">
      <span className="h-7 w-7 rounded-full bg-amber-400 text-navy grid place-items-center shrink-0 mt-0.5 font-extrabold text-[12px]">
        !
      </span>
      <div className="text-[12px] leading-relaxed text-navy/90">
        <div className="font-extrabold text-[12.5px] mb-1">
          {lang === "hi"
            ? "महत्वपूर्ण सूचना — Planning / Reference Only"
            : "Important Notice — Planning / Reference Only"}
        </div>
        <div className="font-medium mb-1">{COA_DISCLAIMER_EN}</div>
        <div className="font-medium text-navy/80">{COA_DISCLAIMER_HI}</div>
      </div>
    </div>
    <label
      className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl border-2 border-amber-400 bg-white px-3 py-2.5"
      data-testid="coa-acceptance-label"
    >
      <input
        type="checkbox"
        checked={accepted}
        onChange={(e) => onAccept(e.target.checked)}
        data-testid="coa-acceptance-checkbox"
        className="mt-0.5 h-4 w-4 accent-navy shrink-0"
      />
      <span className="text-[12.5px] font-bold text-navy leading-snug">
        {lang === "hi"
          ? "मैं समझता/समझती हूँ कि यह एक conceptual design है और इसके लिए licensed professional से site-specific verification ज़रूरी है।"
          : "I understand that this is a conceptual design and requires professional site-specific verification."}
      </span>
    </label>
  </div>
);

const ReceiptPanel = ({ receipt, design, lang }) => {
  const [accepted, setAccepted] = useState(false);
  // Build a download URL that routes through the server-side watermarker for
  // PDFs (so the disclaimer is burned in) and falls back to the raw URL for
  // non-PDF assets like PNG/JPG plans.
  const watermarkedHref = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    if (!/\.pdf(\?|$)/i.test(rawUrl)) return rawUrl;
    return `${API}/library-plan-download?receipt_no=${encodeURIComponent(
      receipt.receipt_no
    )}&url=${encodeURIComponent(rawUrl)}`;
  };
  const guard = (e) => {
    if (!accepted) {
      e.preventDefault();
      // Smooth-scroll the disclaimer into view + flash it
      const el = document.querySelector('[data-testid="coa-disclaimer-block"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-amber-300");
        setTimeout(() => el.classList.remove("ring-4", "ring-amber-300"), 1200);
      }
    }
  };
  const lockedClasses = "bg-sand-alt text-muted-foreground cursor-not-allowed shadow-none";
  const primaryActive = "bg-navy text-gold hover:bg-navy-deep active:scale-[0.99] transition-all";
  const secondaryActive = "border-2 border-navy bg-white text-navy hover:bg-navy hover:text-gold transition-colors";
  return (
  <div className="p-5 space-y-4" data-testid="checkout-success">
    <div className="flex items-center gap-3">
      <div className="h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <div>
        <div className="font-extrabold text-navy">{t(lang, "store_success_title")}</div>
        <div className="text-[12.5px] text-muted-foreground">
          {t(lang, "store_success_sub")}
        </div>
      </div>
    </div>
    <div className="rounded-xl bg-sand-alt border border-border p-3 text-[12.5px] space-y-1">
      <div>
        <span className="text-muted-foreground">{t(lang, "store_receipt_no")}:</span>{" "}
        <span className="font-mono font-bold text-navy">{receipt.receipt_no}</span>
      </div>
      <div>
        <span className="text-muted-foreground">{t(lang, "store_paid")}:</span>{" "}
        <span className="font-bold text-navy">{formatINR(receipt.amount)}</span>
      </div>
    </div>
    {/* CoA disclaimer + mandatory acceptance — gates ALL downloads below */}
    <ComplianceDisclaimer lang={lang} accepted={accepted} onAccept={setAccepted} />
    <a
      href={accepted ? watermarkedHref(receipt.plan_url) : "#"}
      onClick={guard}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={!accepted}
      data-testid="download-plan-btn"
      className={`w-full min-h-[56px] rounded-2xl font-extrabold text-[15px] flex items-center justify-center gap-2 ${
        accepted ? primaryActive : lockedClasses
      }`}
    >
      <Download className="h-5 w-5" />
      {accepted ? (
        receipt.plan_files && receipt.plan_files.length > 1
          ? receipt.plan_files[0].label
          : t(lang, "store_download_plan")
      ) : lang === "hi" ? (
        "Disclaimer accept करें"
      ) : (
        "Accept disclaimer to download"
      )}
    </a>
    {receipt.plan_files && receipt.plan_files.length > 1 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="receipt-extra-plans">
        {receipt.plan_files.slice(1).map((f, i) => (
          <a
            key={f.label + i}
            href={accepted ? watermarkedHref(f.url) : "#"}
            onClick={guard}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!accepted}
            data-testid={`download-plan-${i + 1}-btn`}
            className={`min-h-[48px] rounded-2xl font-extrabold text-[13.5px] flex items-center justify-center gap-2 ${
              accepted ? secondaryActive : lockedClasses
            }`}
          >
            <Download className="h-4 w-4" />
            {f.label}
          </a>
        ))}
      </div>
    )}
    {/* Branded PDF invoice for accounting / GST */}
    <a
      href={`${API}/library/purchases/${receipt.receipt_no}/invoice`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="download-invoice-btn"
      className="w-full min-h-[44px] rounded-xl border-2 border-gold bg-gold/10 text-navy font-extrabold text-[13.5px] flex items-center justify-center gap-2 hover:bg-gold hover:text-navy transition-colors"
    >
      <Download className="h-4 w-4" />
      {lang === "hi" ? "इनवॉइस डाउनलोड (PDF)" : "Download Invoice (PDF)"}
    </a>
    <a
      href={`https://wa.me/${WA_PHONE}?text=${encodeURIComponent(
        lang === "hi"
          ? `नमस्ते 🙏 मैंने ${design.name} (${design.size_label}) नक्शा खरीदा है। रसीद: ${receipt.receipt_no}`
          : `Hi 🙏 I purchased ${design.name} (${design.size_label}). Receipt: ${receipt.receipt_no}`
      )}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-after-purchase"
      className="w-full min-h-[48px] rounded-2xl bg-whatsapp text-white font-bold text-[14px] flex items-center justify-center gap-2 hover:bg-whatsapp-hover transition-all"
    >
      <FaWhatsapp className="h-5 w-5" />
      {t(lang, "store_send_wa")}
    </a>
  </div>
  );
};

export { CheckoutModal };
export default CheckoutModal;
